import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Table,
  Tag,
  Typography,
  Statistic,
  Space,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Tabs,
  Progress,
  Tooltip,
  message,
  Popconfirm,
  Badge,
  Alert,
  Upload
} from 'antd';
import {
  IdcardOutlined,
  UsergroupAddOutlined,
  PlusOutlined,
  ReloadOutlined,
  DisconnectOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  InboxOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import mctCollaboratorService, {
  MctCollaboratorItem,
  MctCollaboratorStats,
  MctEntourageOrder,
  MctCategory
} from '../services/mctCollaboratorService';

dayjs.locale('fr');

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const CATEGORY_COLORS: Record<MctCategory, string> = {
  cadre: '#722ed1',         // Violet prestige
  agent_maitrise: '#1677ff', // Bleu pro
  employe: '#0a543d',        // Émeraude MCT
  ouvrier: '#d46b08'         // Ambre technique
};

const CATEGORY_LABELS: Record<MctCategory, string> = {
  cadre: 'Cadre (25% - 5 splits)',
  agent_maitrise: 'Agent de maîtrise (20% - 4 splits)',
  employe: 'Employé (15% - 3 splits)',
  ouvrier: 'Ouvrier (10% - 2 splits)'
};

const CATEGORY_DEFAULTS: Record<MctCategory, { discount_rate: number; max_splits: number }> = {
  cadre: { discount_rate: 25, max_splits: 5 },
  agent_maitrise: { discount_rate: 20, max_splits: 4 },
  employe: { discount_rate: 15, max_splits: 3 },
  ouvrier: { discount_rate: 10, max_splits: 2 }
};

const MctCollaboratorsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('collaborators');
  const [collaborators, setCollaborators] = useState<MctCollaboratorItem[]>([]);
  const [entourageOrders, setEntourageOrders] = useState<MctEntourageOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [stats, setStats] = useState<MctCollaboratorStats | null>(null);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [entouragePagination, setEntouragePagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // Modals
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCollab, setEditingCollab] = useState<MctCollaboratorItem | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Modal Import Excel
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedCollaborators, setParsedCollaborators] = useState<Array<{
    matricule: string;
    first_name: string;
    last_name: string;
    category: MctCategory;
    rawCsp: string;
  }>>([]);
  const [importFileName, setImportFileName] = useState('');

  // Charger les stats
  const fetchStats = async () => {
    try {
      const res = await mctCollaboratorService.getStats();
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats collaborateurs:', err);
    }
  };

  // Charger la liste des collaborateurs
  const fetchCollaborators = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await mctCollaboratorService.getCollaborators({
        page,
        limit: 20,
        search: search || undefined,
        category: categoryFilter,
        status: statusFilter
      });
      setCollaborators(res.data);
      setPagination(prev => ({
        ...prev,
        current: res.pagination.page,
        total: res.pagination.total
      }));
    } catch (err: any) {
      message.error('Impossible de charger les collaborateurs MCT');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  // Charger les commandes de l'entourage
  const fetchEntourageOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await mctCollaboratorService.getEntourageOrders(page, 20);
      setEntourageOrders(res.data);
      setEntouragePagination(prev => ({
        ...prev,
        current: res.pagination.page,
        total: res.pagination.total
      }));
    } catch (err) {
      message.error('Impossible de charger les commandes de l\'entourage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'collaborators') {
      fetchCollaborators(1);
    } else {
      fetchEntourageOrders(1);
    }
  }, [activeTab, fetchCollaborators, fetchEntourageOrders]);

  const handleOpenAddModal = () => {
    setEditingCollab(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'employe',
      discount_rate: 15,
      max_splits: 3
    });
    setIsModalVisible(true);
  };

  const handleOpenEditModal = (collab: MctCollaboratorItem) => {
    setEditingCollab(collab);
    form.resetFields();
    form.setFieldsValue({
      matricule: collab.matricule,
      first_name: collab.first_name,
      last_name: collab.last_name,
      phone: collab.phone,
      category: collab.category,
      discount_rate: collab.discount_rate,
      max_splits: collab.max_splits,
      used_splits: collab.used_splits,
      is_active: collab.is_active,
      notes: collab.notes
    });
    setIsModalVisible(true);
  };

  const handleCategoryChange = (val: MctCategory) => {
    if (CATEGORY_DEFAULTS[val]) {
      form.setFieldsValue({
        discount_rate: CATEGORY_DEFAULTS[val].discount_rate,
        max_splits: CATEGORY_DEFAULTS[val].max_splits
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingCollab) {
        await mctCollaboratorService.updateCollaborator(editingCollab.id, values);
        message.success(`Collaborateur ${editingCollab.matricule} mis à jour avec succès`);
      } else {
        await mctCollaboratorService.createCollaborator(values);
        message.success(`Collaborateur ${values.matricule.toUpperCase()} créé avec succès`);
      }

      setIsModalVisible(false);
      fetchCollaborators(pagination.current);
      fetchStats();
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message || 'Erreur lors de l\'enregistrement';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async (id: number, matricule: string) => {
    try {
      await mctCollaboratorService.unlinkCollaborator(id);
      message.success(`Matricule ${matricule} dissocié du compte utilisateur. Il est à nouveau disponible.`);
      fetchCollaborators(pagination.current);
      fetchStats();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Erreur lors de la dissociation');
    }
  };

  const handleDelete = async (id: number, matricule: string) => {
    try {
      await mctCollaboratorService.deleteCollaborator(id);
      message.success(`Collaborateur ${matricule} supprimé avec succès`);
      fetchCollaborators(pagination.current);
      fetchStats();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // Traitement du fichier Excel / CSV
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!rawJson || rawJson.length === 0) {
          message.error('Le fichier Excel ne contient aucune ligne de données.');
          return;
        }

        const sampleRow = rawJson[0];
        const keys = Object.keys(sampleRow);

        const nomKey = keys.find(k => /noms?$/i.test(k.trim()) || (/nom/i.test(k) && !/prenom/i.test(k)));
        const prenomKey = keys.find(k => /pr[eé]noms?/i.test(k.trim()));
        const mleKey = keys.find(k => /mle|matricule|code/i.test(k.trim()));
        const cspKey = keys.find(k => /csp|cat[eé]gorie|poste|statut/i.test(k.trim()));

        if (!mleKey) {
          message.error('Colonne « Mle » ou « Matricule » introuvable dans le fichier.');
          return;
        }

        const parsed: Array<{
          matricule: string;
          first_name: string;
          last_name: string;
          category: MctCategory;
          rawCsp: string;
        }> = [];

        rawJson.forEach(row => {
          const rawMle = String(row[mleKey] || '').trim();
          if (!rawMle) return;

          let formattedMatricule = rawMle.toUpperCase();
          if (!formattedMatricule.startsWith('MCT-')) {
            formattedMatricule = `MCT-${formattedMatricule}`;
          }

          const rawNom = nomKey ? String(row[nomKey] || '').trim() : '';
          const rawPrenom = prenomKey ? String(row[prenomKey] || '').trim() : '';
          const rawCsp = cspKey ? String(row[cspKey] || '').trim() : '';

          let category: MctCategory = 'employe';
          const lowerCsp = rawCsp.toLowerCase();
          if (lowerCsp.includes('cadre')) {
            category = 'cadre';
          } else if (lowerCsp.includes('maitrise') || lowerCsp.includes('maîtrise')) {
            category = 'agent_maitrise';
          } else if (lowerCsp.includes('ouvrier')) {
            category = 'ouvrier';
          } else if (lowerCsp.includes('employe') || lowerCsp.includes('employé')) {
            category = 'employe';
          }

          parsed.push({
            matricule: formattedMatricule,
            last_name: rawNom || 'N/A',
            first_name: rawPrenom || 'N/A',
            category,
            rawCsp
          });
        });

        if (parsed.length === 0) {
          message.warning('Aucun collaborateur valide trouvé avec un matricule renseigné.');
          return;
        }

        setParsedCollaborators(parsed);
        setImportFileName(file.name);
        message.success(`${parsed.length} collaborateurs analysés avec succès !`);
      } catch (err: any) {
        message.error(`Erreur de lecture du fichier Excel: ${err?.message || 'Format invalide'}`);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleConfirmImport = async () => {
    if (parsedCollaborators.length === 0) return;
    try {
      setImporting(true);
      const res = await mctCollaboratorService.bulkImportCollaborators(
        parsedCollaborators.map(c => ({
          matricule: c.matricule,
          first_name: c.first_name,
          last_name: c.last_name,
          category: c.category
        }))
      );
      message.success(res.message || `${parsedCollaborators.length} collaborateurs importés avec succès`);
      setIsImportModalVisible(false);
      setParsedCollaborators([]);
      setImportFileName('');
      fetchCollaborators(1);
      fetchStats();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Erreur lors de l\'importation');
    } finally {
      setImporting(false);
    }
  };

  // Colonnes Collaborateurs
  const collaboratorColumns = [
    {
      title: 'Matricule',
      dataIndex: 'matricule',
      key: 'matricule',
      render: (matricule: string) => (
        <Space direction="vertical" size={2}>
          <Tag color="cyan" style={{ fontWeight: 'bold', fontSize: 13, letterSpacing: 0.5 }}>
            {matricule}
          </Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>1 seul compte max</Text>
        </Space>
      )
    },
    {
      title: 'Collaborateur',
      key: 'name',
      render: (_: any, r: MctCollaboratorItem) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>{r.first_name} {r.last_name}</Text>
          {r.phone && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>📞 {r.phone}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Catégorie & Remise',
      key: 'category',
      render: (_: any, r: MctCollaboratorItem) => (
        <Space direction="vertical" size={2}>
          <Tag color={CATEGORY_COLORS[r.category]} style={{ fontWeight: 'bold' }}>
            {r.category.toUpperCase().replace('_', ' ')}
          </Tag>
          <Badge status="processing" color="#0a543d" text={<Text strong style={{ color: '#0a543d' }}>-{r.discount_rate}%</Text>} />
        </Space>
      )
    },
    {
      title: 'Splits Couverts',
      key: 'splits',
      width: 170,
      render: (_: any, r: MctCollaboratorItem) => {
        const percent = Math.min(100, Math.round((r.used_splits / (r.max_splits || 1)) * 100));
        const isFull = r.used_splits >= r.max_splits;
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 12 }}>{r.used_splits} / {r.max_splits} splits</Text>
              <Text style={{ fontSize: 11, color: isFull ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
                {isFull ? 'Épuisé' : `${r.remaining_splits} restant(s)`}
              </Text>
            </div>
            <Progress
              percent={percent}
              size="small"
              status={isFull ? 'exception' : 'active'}
              strokeColor={isFull ? '#ff4d4f' : '#0a543d'}
            />
          </div>
        );
      }
    },
    {
      title: 'Compte SMART App',
      key: 'user',
      render: (_: any, r: MctCollaboratorItem) => {
        if (!r.is_linked || !r.user) {
          return (
            <Tooltip title="Le collaborateur n'a pas encore connecté son application avec ce numéro/matricule">
              <Tag icon={<ClockCircleOutlined />} color="default">
                Non rattaché
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Space direction="vertical" size={2}>
            <Tag icon={<CheckCircleOutlined />} color="success">
              {r.user.name || r.user.email || r.user.phone}
            </Tag>
            {r.linked_at && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Lié le {dayjs(r.linked_at).format('DD/MM/YYYY')}
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Filleuls (Entourage)',
      key: 'referrals',
      render: (_: any, r: MctCollaboratorItem) => (
        <Tooltip title="Commandes passées par ses proches avec son code (-10% bienvenue)">
          <Space>
            <Tag color="purple">
              {r.referrals_count} commande(s)
            </Tag>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Actif' : 'Suspendu'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: MctCollaboratorItem) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(r)}
          >
            Modifier
          </Button>

          {r.is_linked && (
            <Popconfirm
              title="Dissocier ce compte ?"
              description={`Le matricule ${r.matricule} sera libéré et pourra être rattaché à un autre compte.`}
              onConfirm={() => handleUnlink(r.id, r.matricule)}
              okText="Dissocier"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Libérer le matricule (ex: changement de numéro)">
                <Button size="small" danger icon={<DisconnectOutlined />}>
                  Délier
                </Button>
              </Tooltip>
            </Popconfirm>
          )}

          <Popconfirm
            title="Supprimer ce collaborateur ?"
            description={`Êtes-vous sûr de vouloir supprimer ${r.matricule} (${r.first_name} ${r.last_name}) ? Cette action est irréversible.`}
            onConfirm={() => handleDelete(r.id, r.matricule)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Colonnes Entourage
  const entourageColumns = [
    {
      title: 'Filleul (Proche)',
      key: 'filleul',
      render: (_: any, r: MctEntourageOrder) => (
        <div>
          <Text strong>{r.filleul?.first_name} {r.filleul?.last_name}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.filleul?.phone ? `📞 ${r.filleul.phone}` : r.filleul?.email}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Parrain (Collaborateur MCT)',
      key: 'collaborator',
      render: (_: any, r: MctEntourageOrder) => (
        <div>
          <Space>
            <Tag color="cyan">{r.collaborator?.matricule}</Tag>
            <Text>{r.collaborator?.first_name} {r.collaborator?.last_name}</Text>
          </Space>
          <div>
            <Tag color={CATEGORY_COLORS[r.collaborator?.category]} style={{ fontSize: 10 }}>
              {r.collaborator?.category?.toUpperCase()}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Secteur d\'Intervention',
      dataIndex: 'sector',
      key: 'sector',
      render: (sector: string) => {
        const labels: Record<string, { label: string; color: string }> = {
          maintenance: { label: 'Entretien (1ère fois)', color: 'green' },
          installation: { label: 'Installation (1ère fois)', color: 'blue' },
          repair: { label: 'Dépannage (1ère fois)', color: 'orange' }
        };
        const s = labels[sector] || { label: sector, color: 'default' };
        return <Tag color={s.color} style={{ fontWeight: 'bold' }}>{s.label}</Tag>;
      }
    },
    {
      title: 'Réduction Accordée',
      key: 'discount',
      render: (_: any, r: MctEntourageOrder) => (
        <div>
          <Tag color="#0a543d" style={{ fontWeight: 'bold' }}>-10% BIENVENUE</Tag>
          <div style={{ marginTop: 2 }}>
            <Text strong style={{ color: '#0a543d' }}>
              -{Math.round(Number(r.discount_amount)).toLocaleString('fr-FR')} FCFA
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Intervention',
      key: 'intervention',
      render: (_: any, r: MctEntourageOrder) => {
        if (!r.intervention) return <Text type="secondary">N/A</Text>;
        return (
          <div>
            <Text strong>{r.intervention.reference || `#${r.intervention.id}`}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>{r.intervention.title}</Text>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Date de Commande',
      dataIndex: 'used_at',
      key: 'used_at',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm')
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#0a543d' }}>
            <SafetyCertificateOutlined style={{ marginRight: 10 }} />
            Programme Collaborateurs MCT & Ambassadeurs
          </Title>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchCollaborators(pagination.current); }}>
            Actualiser
          </Button>
          <Button
            icon={<FileExcelOutlined style={{ color: '#0a543d' }} />}
            onClick={() => {
              setParsedCollaborators([]);
              setImportFileName('');
              setIsImportModalVisible(true);
            }}
          >
            Importer Excel
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ backgroundColor: '#0a543d', borderColor: '#0a543d' }}
            onClick={handleOpenAddModal}
          >
            Nouveau Collaborateur
          </Button>
        </Space>
      </div>

      {/* Cartes de KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Statistic
              title="Collaborateurs MCT"
              value={stats?.total_collaborators || 0}
              prefix={<IdcardOutlined style={{ color: '#0a543d' }} />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>({stats?.linked_collaborators || 0} liés)</Text>}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Statistic
              title="Splits Couverts Accordés"
              value={stats?.splits?.total_max || 0}
              prefix={<AppstoreOutlined style={{ color: '#1677ff' }} />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>({stats?.splits?.total_used || 0} consommés)</Text>}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Statistic
              title="Commandes Entourage (10%)"
              value={stats?.entourage?.total_orders || 0}
              prefix={<UsergroupAddOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Statistic
              title="Économies Entourage"
              value={stats?.entourage?.total_savings || 0}
              precision={0}
              suffix="FCFA"
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Onglets Collaborateurs / Entourage */}
      <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'collaborators',
              label: (
                <span>
                  <IdcardOutlined />
                  Collaborateurs MCT ({stats?.total_collaborators || 0})
                </span>
              ),
              children: (
                <>
                  {/* Filtres de recherche */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={10}>
                      <Search
                        placeholder="Recherche par matricule, nom, prénom ou téléphone..."
                        allowClear
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onSearch={() => fetchCollaborators(1)}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Select
                        placeholder="Catégorie"
                        allowClear
                        style={{ width: '100%' }}
                        value={categoryFilter}
                        onChange={val => setCategoryFilter(val)}
                      >
                        <Option value="cadre">Cadre (25%)</Option>
                        <Option value="agent_maitrise">Agent de maîtrise (20%)</Option>
                        <Option value="employe">Employé (15%)</Option>
                        <Option value="ouvrier">Ouvrier (10%)</Option>
                      </Select>
                    </Col>
                    <Col xs={12} sm={4}>
                      <Select
                        placeholder="Statut"
                        allowClear
                        style={{ width: '100%' }}
                        value={statusFilter}
                        onChange={val => setStatusFilter(val)}
                      >
                        <Option value="active">Actif</Option>
                        <Option value="inactive">Suspendu</Option>
                      </Select>
                    </Col>
                  </Row>

                  <Table
                    rowKey="id"
                    columns={collaboratorColumns}
                    dataSource={collaborators}
                    loading={loading}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onChange: p => fetchCollaborators(p),
                      showTotal: total => `Total : ${total} collaborateurs`
                    }}
                  />
                </>
              )
            },
            {
              key: 'entourage',
              label: (
                <span>
                  <UsergroupAddOutlined />
                  Commandes Entourage Ambassadeurs ({stats?.entourage?.total_orders || 0})
                </span>
              ),
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="Règle d'or de l'entourage MCT :"
                    description="Tout proche utilisant le code de parrainage d'un collaborateur MCT bénéficie de 10% de réduction automatique sur sa PREMIÈRE intervention dans chaque secteur (Entretien, Installation, Dépannage)."
                    style={{ marginBottom: 16 }}
                  />

                  <Table
                    rowKey="id"
                    columns={entourageColumns}
                    dataSource={entourageOrders}
                    loading={loading}
                    pagination={{
                      current: entouragePagination.current,
                      pageSize: entouragePagination.pageSize,
                      total: entouragePagination.total,
                      onChange: p => fetchEntourageOrders(p),
                      showTotal: total => `Total : ${total} commandes entourage`
                    }}
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      {/* Modal Ajout / Modification Collaborateur */}
      <Modal
        title={editingCollab ? `Modifier le collaborateur ${editingCollab.matricule}` : 'Nouveau Collaborateur MCT'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={submitting}
        okText="Enregistrer"
        cancelText="Annuler"
        width={550}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="matricule"
                label="Numéro Matricule MCT"
                tooltip="Ce matricule est UNIQUE et ne pourra être rattaché qu'à un seul compte"
                rules={[
                  { required: true, message: 'Le matricule est obligatoire' },
                  { pattern: /^[A-Za-z0-9_-]+$/, message: 'Caractères alphanumériques uniquement' }
                ]}
              >
                <Input
                  placeholder="Ex: MCT-1042"
                  disabled={!!editingCollab}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="category"
                label="Catégorie Professionnelle"
                rules={[{ required: true, message: 'Sélectionnez la catégorie' }]}
              >
                <Select onChange={handleCategoryChange}>
                  <Option value="cadre">{CATEGORY_LABELS.cadre}</Option>
                  <Option value="agent_maitrise">{CATEGORY_LABELS.agent_maitrise}</Option>
                  <Option value="employe">{CATEGORY_LABELS.employe}</Option>
                  <Option value="ouvrier">{CATEGORY_LABELS.ouvrier}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[{ required: true, message: 'Le prénom est requis' }]}
              >
                <Input placeholder="Prénom du salarié" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Nom de famille"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Nom du salarié" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Numéro de Téléphone"
                tooltip="Permet la liaison automatique dès que le salarié se connecte avec ce numéro"
              >
                <Input placeholder="Ex: 0701020304" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="discount_rate"
                label="Remise (%)"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="max_splits"
                label="Max Splits"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {editingCollab && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="used_splits" label="Splits Consommés">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="is_active" label="Statut du droit">
                  <Select>
                    <Option value={true}>Actif</Option>
                    <Option value={false}>Suspendu</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item name="notes" label="Notes internes (RH / Administration)">
            <Input.TextArea rows={2} placeholder="Commentaires ou affectation service..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Import Excel / CSV */}
      <Modal
        title={
          <Space>
            <FileExcelOutlined style={{ color: '#0a543d', fontSize: 20 }} />
            <span>Importer des Collaborateurs (Excel / CSV)</span>
          </Space>
        }
        open={isImportModalVisible}
        onCancel={() => {
          if (!importing) {
            setIsImportModalVisible(false);
            setParsedCollaborators([]);
            setImportFileName('');
          }
        }}
        footer={[
          <Button key="cancel" disabled={importing} onClick={() => setIsImportModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            style={{ backgroundColor: '#0a543d', borderColor: '#0a543d' }}
            loading={importing}
            disabled={parsedCollaborators.length === 0}
            onClick={handleConfirmImport}
          >
            Importer {parsedCollaborators.length > 0 ? `(${parsedCollaborators.length})` : ''}
          </Button>
        ]}
        width={750}
      >
        <div style={{ marginBottom: 16 }}>
          <Upload.Dragger
            accept=".xlsx,.xls,.csv"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            style={{ padding: 20, background: '#fafafa' }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 44, color: '#0a543d' }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500 }}>
              Cliquez ou glissez-déposez votre fichier Excel / CSV ici
            </p>
            <p className="ant-upload-hint" style={{ color: '#888' }}>
              Colonnes détectées automatiquement : <b>Noms</b>, <b>Prénoms</b>, <b>Mle</b> (Matricule), <b>CSP</b> (Catégorie Socio-Professionnelle).
              <br />
              Les matricules sont automatiquement préfixés par <code>MCT-</code> s'ils ne le sont pas déjà.
            </p>
          </Upload.Dragger>
        </div>

        {parsedCollaborators.length > 0 && (
          <div>
            <Alert
              type="success"
              showIcon
              message={`${parsedCollaborators.length} collaborateurs prêts à être importés (${importFileName})`}
              description={
                <Space wrap size={[8, 8]} style={{ marginTop: 8 }}>
                  <Tag color={CATEGORY_COLORS.cadre}>
                    {parsedCollaborators.filter(c => c.category === 'cadre').length} Cadres (-25%)
                  </Tag>
                  <Tag color={CATEGORY_COLORS.agent_maitrise}>
                    {parsedCollaborators.filter(c => c.category === 'agent_maitrise').length} Agents de Maîtrise (-20%)
                  </Tag>
                  <Tag color={CATEGORY_COLORS.employe}>
                    {parsedCollaborators.filter(c => c.category === 'employe').length} Employés (-15%)
                  </Tag>
                  <Tag color={CATEGORY_COLORS.ouvrier}>
                    {parsedCollaborators.filter(c => c.category === 'ouvrier').length} Ouvriers (-10%)
                  </Tag>
                </Space>
              }
              style={{ marginBottom: 16 }}
            />

            <Table
              size="small"
              rowKey="matricule"
              dataSource={parsedCollaborators}
              pagination={{ pageSize: 5, size: 'small', showTotal: t => `Total : ${t} personnes` }}
              columns={[
                {
                  title: 'Matricule',
                  dataIndex: 'matricule',
                  key: 'matricule',
                  width: 120,
                  render: (m: string) => <Tag color="cyan" style={{ fontWeight: 'bold' }}>{m}</Tag>
                },
                {
                  title: 'Nom & Prénoms',
                  key: 'name',
                  render: (_: any, r: any) => `${r.last_name} ${r.first_name}`
                },
                {
                  title: 'CSP Fichier',
                  dataIndex: 'rawCsp',
                  key: 'rawCsp',
                  render: (csp: string) => <Text type="secondary">{csp || 'Non spécifié'}</Text>
                },
                {
                  title: 'Catégorie Reconnue',
                  key: 'category',
                  render: (_: any, r: any) => (
                    <Tag color={CATEGORY_COLORS[r.category as MctCategory]} style={{ fontWeight: 'bold' }}>
                      {r.category.toUpperCase().replace('_', ' ')}
                    </Tag>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MctCollaboratorsPage;
